import { useEditor, useValue } from "tldraw"
import { Toolbar } from "./Toolbar"
import { StickerShape } from "../../shared/StickerShape"
import { Blend, Hash, Palette, ToggleRight } from "lucide-react"
import { ColorPickerButton } from "./ColorPickerButton"
import { LDPage } from "../../shared/LDPage"
import { Color, Env, Expr } from "../../shared/Expr"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { NumberPickerButton } from "./NumberPickerButton"
// import {
//   ColorPickerTriggerButton,
//   LayerStrokeWidthContainerDiv,
//   Row,
// } from "./LayerSettingsToolbar"
import { ParameterizableControl } from "./ParameterializableControl"
import Slider from "@mui/material/Slider"
import { Var } from "../icons/Var"
import PaletteOutlined from "@mui/icons-material/PaletteOutlined"
import Button from "@mui/material/Button"
import Switch from "@mui/material/Switch"
import ToggleButton from "@mui/material/ToggleButton"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { showPageSettingsDialog } from "../features/app/appSlice"
import { LDGroupShape, LDShape } from "../../shared/LDShape"
import { Checkbox, CSSProperties, styled, TextField } from "@mui/material"
import { CustomDrawShape } from "../../shared/CustomDrawShape"
import { Parameter } from "../../shared/Parameter"
import { isEqual } from "lodash"
import { SelectionListDiv, SelectionToolbar } from "./SelectionToolbar"
import { LDTextShape } from "../../shared/LDTextShape"
import { LDGeoShape } from "../../shared/LDGeoShape"
import { LDLineShape } from "../../shared/LDLineShape"
import { useCallback, useState } from "react"
import { SeamlessInput } from "./SeamlessInput"

import { Tooltip, IconButton } from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { useCurrentLayer } from "../hooks/useCurrentLayer"
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined"
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined"

const Spacer = styled("div")({
  flex: 1,
})

export const Row = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
})

export const ColorPickerTriggerButton = styled("button")({
  width: 30,
  height: 20,
  borderRadius: 2,
})

export const LayerStrokeWidthContainerDiv = styled("div")({
  display: "flex",
  padding: 2,
  alignItems: "center",
  marginLeft: 10,
})

const nameStyle: CSSProperties = {
  padding: "2px 4px",
  borderRadius: 3,
  margin: "0 -4px",
}

type Props = {}

const sliderStyles = {
  padding: "10px 0",
  width: 80,
}
const textFieldStyles = {
  width: 80,
}

export const baseParameters: Record<string, Parameter> = {
  fillColor: {
    name: "Fill Color",
    type: "color",
  },
  strokeColor: {
    name: "Stroke Color",
    type: "color",
  },
  strokeWidth: {
    name: "Stroke Width",
    type: "number",
    min: 0.1,
    max: 20,
    step: 0.1,
  },
  text: {
    name: "Text",
    type: "string",
  },
  roundness: {
    name: "Roundness",
    type: "number",
    min: 0,
    max: 40,
  },
  visible: {
    name: "Visible",
    type: "boolean",
    group: "Visibility & Opacity",
  },
  opacity: {
    name: "Opacity",
    type: "number",
    min: 0,
    max: 1,
    group: "Visibility & Opacity",
  },
  font: {
    name: "Font",
    type: "string",
    options: ["draw", "sans", "serif", "mono"],
  },
}

export function LayerOverridesToolbar(props: Props) {
  const editor = useEditor()
  const page = useCurrentPage()
  const layerId = page.meta.currentLayer

  const stickers = editor
    .getCurrentPageShapes()
    .filter(
      (shape) =>
        LDShape.getLayerId(shape as LDShape) === layerId &&
        LDShape.getSourcePageId(editor, shape as LDShape) === page.id,
    ) as LDShape[]

  const onlySticker = stickers.length === 1 ? stickers[0] : null

  const parameters = useValue(
    "sticker parameters",
    () => {
      let result = {} as Record<
        string,
        {
          name: string
          type: "color" | "number" | "boolean" | "string"
          values: Expr<Color | number | boolean | string>[]
          min?: number
          max?: number
          step?: number
        }
      >

      stickers.forEach((sticker) => {
        const stickerPage =
          sticker.type === "custom-group"
            ? (editor.getPage(sticker.props.pageId) as LDPage)
            : null

        const stickerOverrides: Env = (sticker as CustomDrawShape).props
          .parameterValues

        result = {
          ...result,
          ...Object.fromEntries(
            Object.entries(
              sticker.type === "custom-group"
                ? stickerPage!.meta.parameters
                : baseParameters,
            ).map(([paramId, p]) => {
              const prevValue = result[paramId]?.values
              const newValue = stickerOverrides?.[paramId]
              if (prevValue?.find((v) => isEqual(v, newValue)))
                return [
                  paramId,
                  {
                    name: p.name,
                    type: p.type,
                    values: prevValue,
                    min: p.type === "number" ? p.min : undefined,
                    max: p.type === "number" ? p.max : undefined,
                    step: p.type === "number" ? p.step : undefined,
                  },
                ] as const

              const values = prevValue
                ? newValue
                  ? [...prevValue, newValue]
                  : prevValue
                : newValue
                  ? [newValue]
                  : []

              return [
                paramId,
                {
                  name: p.name,
                  type: p.type,
                  values:
                    values.length === 0 && stickerPage
                      ? [stickerPage?.meta.parameterValues[paramId]]
                      : values,
                  min: p.type === "number" ? p.min : undefined,
                  max: p.type === "number" ? p.max : undefined,
                  step: p.type === "number" ? p.step : undefined,
                },
              ] as const
            }),
          ),
        }
      })

      return result
    },
    [stickers, editor],
  )

  function handleLayerOverrideChange(
    paramId: string,
    value: Expr<Color> | Expr<number> | Expr<boolean> | Expr<string> | null,
  ) {
    editor.updatePage({
      ...page,
      meta: {
        ...page.meta,
        layers: {
          ...page.meta.layers,
          [layerId]: {
            ...page.meta.layers[layerId],
            layerOverrides: {
              ...page.meta.layers[layerId].layerOverrides,
              [paramId]: value,
            },
          },
        },
      },
    })
  }

  const selectedShapeIds = useValue(
    "selected shape ids",
    () => editor.getSelectedShapeIds(),
    [editor],
  )

  const pageMeta = page.meta
  const [isEditingName, setIsEditingName] = useState(false)
  const currentLayer = useCurrentLayer()

  const opacity = currentLayer.opacity
  const visible = currentLayer.visible

  const parameterValues = pageMeta.parameterValues

  const handleOpacityChange = useCallback(
    (value: Expr<number>) => {
      const layers = {
        ...pageMeta.layers,
      }

      layers[pageMeta.currentLayer] = {
        ...layers[pageMeta.currentLayer],
        opacity: value,
      }

      editor.updatePage({ id: page.id, meta: { ...pageMeta, layers } })
    },
    [page.id, pageMeta],
  )

  const handleVisibilityChange = useCallback(
    (value: Expr<boolean>) => {
      const layers = {
        ...pageMeta.layers,
      }

      layers[pageMeta.currentLayer] = {
        ...layers[pageMeta.currentLayer],
        visible: value,
      }

      editor.updatePage({ id: page.id, meta: { ...pageMeta, layers } })
    },
    [page.id, pageMeta],
  )

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <SelectionListDiv>{stickers.length} shapes</SelectionListDiv>
      <Toolbar direction="vertical">
        {/* <SelectionToolbar /> */}
        <div
          style={{
            display: "flex",
            gap: 12,
            flexDirection: "column",
            padding: "12px",
            //borderRadius: "12px",
            //background: "linear-gradient(145deg, #f0f0f0, #ffffff)",
          }}
        >
          <div style={{ fontSize: "18px", flex: 1, paddingRight: 40 }}>
            {isEditingName ? (
              <SeamlessInput
                style={nameStyle}
                value={currentLayer.name}
                onBlur={(newVal) => {
                  const layers = {
                    ...pageMeta.layers,
                  }
                  layers[pageMeta.currentLayer] = {
                    ...layers[pageMeta.currentLayer],
                    name: newVal,
                  }

                  editor.updatePage({
                    id: page.id,
                    meta: { ...pageMeta, layers },
                  })
                  setIsEditingName(false)
                }}
                onInputChange={() => {}}
              />
            ) : (
              <div onClick={() => setIsEditingName(true)}>
                {currentLayer.name}
              </div>
            )}
          </div>

          <ParameterizableControl
            type="boolean"
            title="Visibility"
            expr={visible}
            value={visible.type === "ref" ? visible.id : null}
            bound={visible.type !== "lit"}
            onChange={(value) => {
              if (value === null) {
                handleVisibilityChange(
                  Expr.lit(Expr.evaluate(visible, parameterValues)),
                )
              } else {
                handleVisibilityChange(value)
              }
            }}
          >
            <IconButton
              onClick={() =>
                handleVisibilityChange(
                  Expr.lit(
                    !Expr.evaluate(currentLayer.visible, parameterValues),
                  ),
                )
              }
              size="small"
              disabled={visible.type === "ref"}
              style={{
                background: visible.type === "ref" ? "lightgrey" : undefined,
              }}
            >
              <Tooltip
                title={
                  Expr.evaluate(currentLayer.visible, parameterValues)
                    ? "Hide Layer"
                    : "Show Layer"
                }
              >
                {Expr.evaluate(currentLayer.visible, parameterValues) ? (
                  <VisibilityOutlined
                    fontSize="inherit"
                    style={{ color: "rgba(0,0,0,.8)" }}
                  />
                ) : (
                  <VisibilityOffOutlined
                    fontSize="inherit"
                    style={{ color: "rgba(0,0,0,.8)" }}
                  />
                )}
              </Tooltip>
            </IconButton>
          </ParameterizableControl>
          <ParameterizableControl
            type="number"
            icon={<Blend size={14} />}
            title="Opacity"
            value={opacity.type === "ref" ? opacity.id : null}
            expr={opacity}
            bound={opacity.type !== "lit"}
            onChange={(value) => {
              if (value === null) {
                handleOpacityChange(
                  Expr.lit(Expr.evaluate(opacity, parameterValues)),
                )
              } else {
                handleOpacityChange(value)
              }
            }}
          >
            <LayerStrokeWidthContainerDiv>
              <Slider
                size="small"
                value={Expr.evaluate(opacity, parameterValues)}
                style={sliderStyles}
                onChange={(_, value) => handleOpacityChange(Expr.lit(value))}
                min={0}
                step={0.01}
                max={1}
              />

              <div
                style={{
                  width: 30,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                }}
              >
                {Expr.evaluate(opacity, parameterValues)}
              </div>
            </LayerStrokeWidthContainerDiv>
          </ParameterizableControl>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div>Overrides</div>
            <Tooltip
              title="Override the properties of all shapes in this layer"
              arrow
            >
              <IconButton size="small">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          {Object.entries(parameters).length === 0 && (
            <div>
              <div>No properties to override</div>
            </div>
          )}
          <div style={{ maxHeight: 100, overflow: "auto" }}>
            {Object.entries(parameters).map(([id, p]) => {
              const firstValue = p.values[0]

              if (!firstValue) return null
              return (
                <div key={id}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Checkbox
                      size="small"
                      style={{ padding: 0 }}
                      onChange={(_, checked) => {
                        handleLayerOverrideChange(
                          id,
                          // casted to expression of any to fix a type error
                          (checked ? firstValue : null) as Expr<any> | null,
                        )
                      }}
                      checked={!!page.meta.layers[layerId].layerOverrides?.[id]}
                    ></Checkbox>
                    {p.type === "color" && <Palette size={14} />}
                    {p.type === "number" && <Hash size={14} />}
                    {p.type === "boolean" && <ToggleRight size={14} />}
                    <span>{p.name}</span>
                  </div>

                  {page.meta.layers[layerId].layerOverrides?.[id] && (
                    <Row style={{ alignItems: "center", gap: 6 }}>
                      <ParameterizableControl
                        type={p.type}
                        title="Stroke Size"
                        value={
                          p.values.length === 1
                            ? firstValue.type === "ref"
                              ? firstValue.id
                              : null
                            : null
                        }
                        expr={firstValue}
                        bound={
                          p.values.length === 1
                            ? firstValue.type !== "lit"
                            : false
                        }
                        onChange={(value) => {
                          if (value === null) {
                            handleLayerOverrideChange(
                              id,
                              Expr.lit(
                                Expr.evaluate<any>(
                                  firstValue,
                                  page.meta.parameterValues,
                                ),
                              ),
                            )
                          } else {
                            handleLayerOverrideChange(id, value)
                          }
                        }}
                      >
                        <LayerStrokeWidthContainerDiv
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {p.type === "string" &&
                            (() => {
                              return (
                                <>
                                  <TextField
                                    size="small"
                                    value={Expr.evaluate(
                                      p.values[0],
                                      page.meta.parameterValues,
                                    )}
                                    style={textFieldStyles}
                                    onChange={(ev) =>
                                      handleLayerOverrideChange(
                                        id,
                                        Expr.lit(ev.target.value as any),
                                      )
                                    }
                                    variant="standard"
                                    disabled={firstValue.type === "ref"}
                                  />
                                </>
                              )
                            })()}
                          {p.type === "boolean" &&
                            (() => {
                              const value = Expr.evaluate(
                                page.meta.layers[layerId].layerOverrides[id],
                                page.meta.parameterValues,
                              )
                              return (
                                <Switch
                                  size="small"
                                  checked={value}
                                  onChange={(ev) =>
                                    handleLayerOverrideChange(
                                      id,
                                      Expr.lit(ev.target.checked),
                                    )
                                  }
                                  disabled={firstValue.type === "ref"}
                                />
                              )
                            })()}
                          {p.type === "number" &&
                            (() => {
                              const value = Expr.evaluate(
                                page.meta.layers[layerId].layerOverrides[id],
                                page.meta.parameterValues,
                              )
                              return (
                                <>
                                  <Slider
                                    size="small"
                                    value={value}
                                    style={sliderStyles}
                                    onChange={(_, value) =>
                                      handleLayerOverrideChange(
                                        id,
                                        Expr.lit(Number(value) as number),
                                      )
                                    }
                                    step={p.step ?? 0.01}
                                    min={p.min ?? 1}
                                    max={p.max ?? 20}
                                    disabled={firstValue.type === "ref"}
                                  />
                                  <div
                                    style={{
                                      width: 15,
                                      fontSize: 12,
                                      fontWeight: 500,
                                      textAlign: "right",
                                      color: "#555",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {value}
                                  </div>
                                </>
                              )
                            })()}

                          {p.type === "color" && (
                            <ColorPickerButton
                              color={
                                page.meta.layers[layerId].layerOverrides[id]
                              }
                              onChange={(color) =>
                                handleLayerOverrideChange(id, color)
                              }
                            >
                              {function (clickHandler, ref, color) {
                                const backgroundColor = Array.isArray(color)
                                  ? multiColorBackground(
                                      color.map((c) =>
                                        Expr.evaluate(
                                          c,
                                          page.meta.parameterValues,
                                        ),
                                      ),
                                    )
                                  : Expr.evaluate(
                                      color,
                                      page.meta.parameterValues,
                                    )

                                return (
                                  <ColorPickerTriggerButton
                                    ref={ref}
                                    onClick={clickHandler}
                                    style={{
                                      background: backgroundColor,
                                      border: "1px solid #ccc",
                                      borderRadius: "6px",
                                      width: 34,
                                      height: 20,
                                      cursor: "pointer",
                                      boxShadow:
                                        "inset 0 0 3px rgba(0,0,0,0.1)",
                                      // display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    {!Array.isArray(color) &&
                                      color.type !== "lit" && <Var size={12} />}
                                  </ColorPickerTriggerButton>
                                )
                              }}
                            </ColorPickerButton>
                          )}
                        </LayerStrokeWidthContainerDiv>
                      </ParameterizableControl>
                    </Row>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </Toolbar>
    </div>
  )
}

function multiColorBackground(colors: Color[]) {
  return `linear-gradient(70deg, ${colors
    .map(function (c, i) {
      const bandSize = 100 / colors.length
      return `${c} ${i * bandSize}% ${i * bandSize + bandSize}%`
    })
    .join(", ")})`
}
